import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { isoDateInZone, recordDays, videoHost, videoUrlWithStart } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalExercise } from '../../db/local.js';
import {
  createVariant,
  deleteExercise,
  getExercise,
  guidanceFor,
  listExercises,
  updateExercise,
} from '../../db/exercises.js';
import { progressOfExercise, recentSessionsOfExercise } from '../../db/workouts.js';
import type { ExerciseProgress } from '../../db/workouts.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { readProfile } from '../../db/profile.js';
import { useAchievementScope } from '../../hooks/useModules.js';
import { formatDay } from '../../format.js';
import { Sparkline } from '../Sparkline.js';
import { ExerciseArtFigure } from './ExerciseArt.js';
import { ExerciseEditor } from './ExerciseEditor.js';
import { formatNumber, summarizeSets } from './SessionScreen.js';
import { useSwipeBack } from '../../gestures/SwipeProvider.js';

/**
 * Eine Übung auf einer Seite — wie ein Gericht: erst lesen, dann bearbeiten.
 *
 * Oben, was man im Gym nachschlägt (Bestwert, letzte Einheiten, Hinweise,
 * Video), darunter Varianten und die Werkzeuge. Hinweise und Videos einer
 * Variante leiht sie sichtbar vom Grundtyp (`guidanceFor`), wie im Training.
 */
export function ExerciseDetail({
  exerciseId,
  onBack,
  onOpen,
}: {
  exerciseId: string;
  onBack: () => void;
  /** Eine andere Übung öffnen — Variante, Grundtyp, gerade angelegte. */
  onOpen: (id: string) => void;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone() ?? 'Europe/Berlin';
  const [mode, setMode] = useState<'read' | 'edit' | 'variant'>('read');

  const exercise = useLiveQuery(() => db.exercises.get(exerciseId), [exerciseId]);
  const all = useLiveQuery(() => listExercises(db), [], [] as LocalExercise[]);
  const parentId = exercise?.parentId ?? null;
  const parent = useLiveQuery(
    async () => (parentId ? await getExercise(parentId, db) : null),
    [parentId],
    null,
  );
  const progress = useLiveQuery(
    async () => (exercise ? progressOfExercise(exercise, db, timeZone) : null),
    [exercise, timeZone],
    null,
  ) as ExerciseProgress | null;
  // Bestwerte (E13) — für Teens mit Training-Freigabe seit 28.10.2026 auch:
  // Das Hantelgewicht ist keine Körperzahl. Kinder darunter: keine.
  const recordsInScope = useAchievementScope().records;
  const achievements =
    useLiveQuery(async () => (await readProfile(db)).achievements, [], false) && recordsInScope;
  const recent = useLiveQuery(
    () => recentSessionsOfExercise(exerciseId, db, { limit: 5 }),
    [exerciseId],
    [],
  );

  if (exercise === undefined) return null;
  if (!exercise || exercise.deletedAt) {
    return (
      <div className="flex flex-col gap-3">
        <BackButton onBack={onBack} />
        <p className="text-sm text-ink-muted">{t('training.detail.gone')}</p>
      </div>
    );
  }

  const variants = all.filter((entry) => entry.parentId === exercise.id);
  const guidance = guidanceFor(exercise, parent);
  const history = progress?.history.slice(-12) ?? [];
  // Rekordtage (E13) — gezeigt nur, wer Erfolge sehen will.
  const records = achievements ? recordDays(progress?.history ?? []) : [];
  const title =
    exercise.parentId && exercise.variantLabel
      ? `${exercise.name} · ${exercise.variantLabel}`
      : exercise.name;

  if (mode === 'edit') {
    return (
      <section aria-label={t('training.library.edit')} className="flex flex-col gap-3">
        <BackButton onBack={() => setMode('read')} />
        <ExerciseEditor
          base={exercise}
          existing
          onCancel={() => setMode('read')}
          onSave={async (draft) => {
            await updateExercise(exercise.id, draft, db);
            setMode('read');
          }}
          onDelete={async () => {
            await deleteExercise(exercise.id, db);
            onBack();
          }}
        />
      </section>
    );
  }

  if (mode === 'variant') {
    return (
      <section aria-label={t('training.library.addVariant')} className="flex flex-col gap-3">
        <BackButton onBack={() => setMode('read')} />
        <ExerciseEditor
          base={exercise}
          variant
          onCancel={() => setMode('read')}
          onSave={async (draft) => {
            const created = await createVariant(exercise.id, draft, db);
            setMode('read');
            if (created) onOpen(created.id);
          }}
        />
      </section>
    );
  }

  return (
    <article aria-labelledby="exercise-title" className="flex flex-col gap-4">
      <BackButton onBack={onBack} />

      <header className="flex flex-col gap-2">
        <h2 id="exercise-title" className="font-display text-2xl font-bold break-words">
          {title}
        </h2>
        <ul className="flex flex-wrap gap-1.5">
          <li className="kw-chip">{t(`training.equipment.${exercise.equipment}`)}</li>
          <li className="kw-chip">{t(`training.mode.${exercise.trackingMode}`)}</li>
          {exercise.primaryMuscles.map((muscle) => (
            <li key={muscle} className="kw-chip">
              {t(`training.muscle.${muscle}`)}
            </li>
          ))}
        </ul>
        {exercise.secondaryMuscles.length > 0 ? (
          <p className="text-xs text-ink-muted">
            {t('training.exercise.secondaryMuscles')}:{' '}
            {exercise.secondaryMuscles.map((muscle) => t(`training.muscle.${muscle}`)).join(', ')}
          </p>
        ) : null}
        {parent ? (
          <button
            type="button"
            onClick={() => onOpen(parent.id)}
            className="self-start text-sm text-accent underline"
          >
            {t('training.detail.variantOf', { name: parent.name })}
          </button>
        ) : null}
      </header>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode('edit')}
          className="kw-btn kw-btn-small flex-1"
        >
          {t('training.library.edit')}
        </button>
        <button
          type="button"
          onClick={() => setMode('variant')}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1 px-3 whitespace-nowrap"
        >
          {t('training.detail.newVariant')}
        </button>
        <button
          type="button"
          aria-pressed={exercise.isFavorite}
          aria-label={t('training.exercise.isFavorite')}
          onClick={() => void updateExercise(exercise.id, { isFavorite: !exercise.isFavorite }, db)}
          className="kw-btn kw-btn-quiet kw-btn-small px-4"
        >
          <span aria-hidden="true" className={exercise.isFavorite ? 'text-accent' : ''}>
            {exercise.isFavorite ? '★' : '☆'}
          </span>
        </button>
      </div>

      <section aria-labelledby="exercise-progress" className="kw-tile flex flex-col gap-2">
        <h3 id="exercise-progress" className="kw-kicker">
          {t('training.detail.progress')}
        </h3>
        {!progress || progress.totalSets === 0 ? (
          <p className="text-sm text-ink-muted">{t('training.detail.noSets')}</p>
        ) : (
          <>
            <p className="text-sm">
              <span className="text-ink-muted">{t('training.progress.oneRepMax')}: </span>
              {progress.bestOneRepMaxKg === null
                ? t('training.progress.noEstimate')
                : t('training.progress.estimate', {
                    best: formatNumber(Math.round(progress.bestOneRepMaxKg)),
                    sets: progress.totalSets,
                  })}
            </p>
            {history.length >= 2 ? (
              <Sparkline
                values={history.map((point) => point.kg)}
                summary={t('training.progress.historySummary', {
                  name: exercise.name,
                  first: formatNumber(Math.round(history[0]!.kg)),
                  last: formatNumber(Math.round(history.at(-1)!.kg)),
                  days: history.length,
                })}
              />
            ) : null}
            {records.length > 0 ? (
              <div className="flex flex-col gap-1">
                <p className="text-xs text-ink-muted">
                  {t('rings.records', { count: records.length })}
                </p>
                <ul className="flex flex-wrap gap-1.5">
                  {records
                    .slice(-6)
                    .reverse()
                    .map((point) => (
                      <li key={point.day} className="kw-chip border-good text-good">
                        {formatDay(point.day, 'date', i18n.language)} · ≈{' '}
                        {formatNumber(Math.round(point.kg * 2) / 2)} kg
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </section>

      {recent.length > 0 ? (
        <section aria-labelledby="exercise-recent" className="kw-tile flex flex-col gap-2">
          <h3 id="exercise-recent" className="kw-kicker">
            {t('training.detail.recent')}
          </h3>
          <ul className="flex flex-col gap-1.5 text-sm">
            {recent.map((entry) => (
              <li key={entry.sessionId} className="flex flex-col">
                <span className="text-xs text-ink-muted">
                  {formatDay(
                    isoDateInZone(new Date(entry.startedAt), timeZone),
                    'date',
                    i18n.language,
                  )}
                </span>
                <span>{summarizeSets(entry.sets, exercise)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section aria-labelledby="exercise-guide" className="kw-tile flex flex-col gap-2 text-sm">
        <h3 id="exercise-guide" className="kw-kicker">
          {t('training.detail.guide')}
        </h3>
        <ExerciseArtFigure catalogKey={exercise.catalogKey} name={exercise.name} />
        {exercise.setupNotes ? (
          <p>
            <span className="text-ink-muted">{t('training.exercise.setupNotes')}: </span>
            {exercise.setupNotes}
          </p>
        ) : null}
        {guidance.cues.length > 0 ? (
          <div>
            {guidance.cuesFrom ? (
              <p className="text-xs text-ink-muted">
                {t('training.exercise.inheritedFrom', { name: guidance.cuesFrom })}
              </p>
            ) : null}
            <ul className="mt-1 list-disc pl-5">
              {guidance.cues.map((cue) => (
                <li key={cue}>{cue}</li>
              ))}
            </ul>
            <p className="mt-1 text-xs text-ink-muted">{t('training.exercise.cuesDisclaimer')}</p>
          </div>
        ) : (
          <p className="text-xs text-ink-muted">{t('training.detail.noCues')}</p>
        )}
        {guidance.videos.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {guidance.videos.map((video) => (
              <li key={video.url}>
                <a
                  href={videoUrlWithStart(video)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-accent underline"
                >
                  {video.label} · {videoHost(video.url) ?? t('training.exercise.unknownHost')}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-ink-muted">{t('training.exercise.noVideo')}</p>
        )}
        {exercise.note ? (
          <p>
            <span className="text-ink-muted">{t('training.exercise.note')}: </span>
            {exercise.note}
          </p>
        ) : null}
      </section>

      {variants.length > 0 ? (
        <section aria-labelledby="exercise-variants" className="kw-tile flex flex-col gap-1 p-0">
          <h3 id="exercise-variants" className="kw-kicker px-4 pt-3">
            {t('training.detail.variants')}
          </h3>
          <ul className="flex flex-col divide-y divide-line">
            {variants.map((variant) => (
              <li key={variant.id}>
                <button
                  type="button"
                  onClick={() => onOpen(variant.id)}
                  className="flex min-h-11 w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm hover:bg-surface-raised"
                >
                  <span className="min-w-0 truncate">{variant.variantLabel ?? variant.name}</span>
                  <span aria-hidden="true" className="text-ink-muted">
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}

function BackButton({ onBack }: { onBack: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  // Wo ein Zurück-Knopf steht, wirkt auch das Wischen vom linken Rand.
  useSwipeBack(onBack);
  return (
    <button
      type="button"
      onClick={onBack}
      className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
    >
      ← {t('training.detail.back')}
    </button>
  );
}
